/**
 * Created by Ning Jiang on 2021-06-17.
 */

import {gsap, TimelineLite} from "gsap";
import {Layers, TombstoneRipLayers} from "../../../view/TombstoneRipLayers";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {TombstoneRip} from "../../../TombstoneRip";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {IBetWayWinData} from "../../../server/data/ITombstoneRipData";
import {MathHelper} from "../../../../core/utils/MathHelper";
import {IndividualWinText} from "../../individual/IndividualWinView";

export class WinEffect extends TimelineSprite {

    public winLevel:number;

    constructor(level:number) {
        super(WinEffect.getLevelTextures(level), 30);
        this.winLevel = level;
    }

    private static getLevelTextures(level:number):PIXI.Texture[] {
        switch(level) {
            case 0:
                return TombstoneRip.assetLoader.getTextures(`initialWin/initialTiny/initT_`);
            case 1:
                return TombstoneRip.assetLoader.getTextures(`initialWin/initialSmall/initS_`);
            case 2:
                return TombstoneRip.assetLoader.getTextures(`initialWin/initialMid/initM_`);
            default:
                return TombstoneRip.assetLoader.getTextures(`initialWin/initialLarge/initL_`);
        }
    }
}

export class BetWaysInitialWinView {

    private _layer:StageLayer;
    private _numberLayer:StageLayer;
    private _effectPool:WinEffect[][] = [[], [], [], []];
    private _playingGlow:PIXI.spine.Spine[] = [];

    constructor() {
        this._layer = TombstoneRipLayers.getLayer(Layers.WIN_PRESENTATIONS)!;
        this._numberLayer = TombstoneRipLayers.getLayer(Layers.WIN_PRESENTATION_NUMBERS)!;
    }

    public playAnimation(position:PIXI.Point, effectLevel:number, doubleReel:boolean, showWinText:boolean, winData:IBetWayWinData):TimelineLite {
        const tl = new TimelineLite();

        if(doubleReel) {
            tl.add([
                this.playAnimation(new PIXI.Point(position.x, position.y - 92), effectLevel, false, showWinText, winData),
                this.playAnimation(new PIXI.Point(position.x, position.y + 92), effectLevel, false, false, winData)
            ]);

            return tl;
        }

        const glow = new PIXI.spine.Spine(TombstoneRip.assetLoader.getSpineAsset("initialGlow"));
        glow.autoUpdate = true;
        glow.position.set(position.x, position.y);

        this._playingGlow.push(glow);

        const effect = this.getEffect(effectLevel);
        effect.hide();
        effect.position.set(position.x, position.y);

        tl.add([
            () => {
                this._layer.addChild(glow);
                this._layer.addChild(effect);
                if(showWinText) {
                    this.playWinText(position, winData);
                }
            },
            effect.getAnimationAutoShowHide(),
            new SpineTween(glow, 0, this.getGlowName(effectLevel))
        ]);
        tl.add(() => this.recycleEffect(effect, glow));

        return tl;
    }

    private playWinText(position:PIXI.Point, winData:IBetWayWinData):void {
        const winText = new IndividualWinText(MathHelper.roundToDecimals(winData.multipliedAmount / winData.multiplier, 2), winData.numberOfCombinations);
        winText.position.set(position.x, position.y);
        const winTextTl = new TimelineLite();
        winTextTl.add(() => {
            this._numberLayer.addChild(winText);
        });
        winTextTl.add([
            gsap.to(winText, {y : position.y - 100, duration : 1}),
            gsap.to(winText, {alpha : 0, duration : 0.8, delay : 0.2})
        ], `+=0.3`);
        winTextTl.add(() => {
            this._numberLayer.removeChild(winText);
            winText.destroy();
        });
    }

    private getGlowName(effectLevel:number):string {
        switch(effectLevel) {
            case 0:
                return "initialTiny";
            case 1:
                return "initialSmall";
            case 2:
                return "initialMid";
            default:
                return "intialLarge";
        }
    }

    private getEffect(effectLevel:number):WinEffect {
        if(this._effectPool[effectLevel].length > 0) {
            return this._effectPool[effectLevel].pop()!;
        }

        const effect = new WinEffect(effectLevel);
        effect.anchor.set(0.5);
        return effect;
    }

    private recycleEffect(effect:WinEffect, glow:PIXI.spine.Spine):void {
        this._layer.removeChild(effect);
        this._effectPool[effect.winLevel].push(effect);

        glow.state.setEmptyAnimation(0, 0);
        this._layer.removeChild(glow);
        ArrayHelper.removeFirstMatchElement(this._playingGlow, glow);
    }
}